import { Directive, Input, TemplateRef, ViewContainerRef, inject } from "@angular/core";
import { AppAction } from "tht-impianti-cl";
import { PermissionService } from "src/app/global-service/permission.service";

/**
 * Mostra un pezzo di template solo se l'azione è permessa a questo utente.
 *
 * Sostituisce gli `*ngIf="userService.canQualcosa()"` sparsi: la condizione smette di essere
 * inventata pagina per pagina e diventa un riferimento alla stessa matrice che usa il backend.
 *
 * Un blocco solo:
 *
 *     <button *appCan="AppActions.UPDATE_FACILITY">Modifica</button>
 *
 * Più azioni, in OR (il default) o in AND:
 *
 *     <div *appCan="[AppActions.CREATE_TICKET, AppActions.UPDATE_TICKET]">…</div>
 *     <div *appCan="[AppActions.READ_FACILITY, AppActions.READ_CHECKLIST]; mode: 'all'">…</div>
 *
 * Con un'alternativa, per i campi che vanno comunque occupati (una cella di tabella, un importo
 * che diventa un trattino):
 *
 *     <span *appCan="AppActions.READ_MAINTENANCE_CONTRACT_ECONOMICS; else: trattino">{{ budget }}</span>
 *     <ng-template #trattino>—</ng-template>
 *
 * DOVE NON ARRIVA: le voci di una tendina e gli elementi di un array sono dati, non template. Per
 * quelli c'è `filterByPermission` in `permission.service.ts`.
 */
@Directive({
    selector: "[appCan]",
    standalone: false
})
export class CanDirective {

    private templateRef = inject<TemplateRef<unknown>>(TemplateRef);
    private viewContainer = inject(ViewContainerRef);
    private permissions = inject(PermissionService);

    private actions: AppAction[] = [];
    private mode: "any" | "all" = "any";
    private elseTemplate: TemplateRef<unknown> | null = null;
    /** null = mai renderizzato: distingue "prima volta" da "già valutato con questo esito" */
    private renderedState: string | null = null;

    @Input()
    set appCan(action: AppAction | AppAction[] | null | undefined) {
        this.actions = action ? (Array.isArray(action) ? action : [action]) : [];
        this.update();
    }

    /** `any` (default) = basta una delle azioni; `all` = servono tutte */
    @Input()
    set appCanMode(mode: "any" | "all") {
        this.mode = mode === "all" ? "all" : "any";
        this.update();
    }

    /** Template alternativo, mostrato quando l'azione non è permessa */
    @Input()
    set appCanElse(template: TemplateRef<unknown> | null) {
        this.elseTemplate = template ?? null;
        this.update();
    }

    private update(): void {
        // senza azioni non si concede nulla: una direttiva con input vuoto è quasi sempre un errore
        // di scrittura, e nasconderla è l'esito meno dannoso
        const allowed = this.actions.length > 0
            && (this.mode === "all"
                ? this.permissions.canAll(...this.actions)
                : this.permissions.canAny(...this.actions));

        // Si ricostruisce la vista solo quando cambia ciò che si vedrebbe: gli input arrivano uno
        // alla volta (appCan, poi mode, poi else) e senza questo controllo il contenuto verrebbe
        // distrutto e ricreato a ogni set, perdendo lo stato dei componenti figli.
        // Nello stato entra anche il template alternativo: se arriva DOPO il primo giro, il
        // contenuto da mostrare cambia pur restando `allowed` a false.
        const state = `${String(allowed)}|${this.elseTemplate ? "else" : "none"}`;
        if (this.renderedState === state) {
            return;
        }
        this.renderedState = state;
        this.viewContainer.clear();
        if (allowed) {
            this.viewContainer.createEmbeddedView(this.templateRef);
        } else if (this.elseTemplate) {
            this.viewContainer.createEmbeddedView(this.elseTemplate);
        }
    }
}
